import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { BrandingProvider } from '@/lib/branding';
import { LoginPage } from '@/pages/Login';
import { ForgotPasswordPage } from '@/pages/ForgotPassword';
import { RegisterPage } from '@/pages/Register';
import { ForceResetPage } from '@/pages/ForceReset';
import { InsurerLayout } from '@/layouts/InsurerLayout';
import { BrokerLayout } from '@/layouts/BrokerLayout';
import { InsurerDashboard } from '@/pages/insurer/Dashboard';
import { BrokersPage } from '@/pages/insurer/Brokers';
import { BrokerDetailPage } from '@/pages/insurer/BrokerDetail';
import { EmptyListPage } from '@/pages/insurer/EmptyList';
import { ClientsPage } from '@/pages/insurer/Clients';
import { ClientDetailPage } from '@/pages/insurer/ClientDetail';
import { SitesPage } from '@/pages/insurer/Sites';
import { PendingSitesPage } from '@/pages/insurer/PendingSites';
import { SiteIntakePage } from '@/pages/public/SiteIntake';
import { SiteDetailPage } from '@/pages/insurer/SiteDetail';
import { CoversPage } from '@/pages/insurer/Covers';
import { CoverBuilderPage } from '@/pages/insurer/CoverBuilder';
import { CoverDetailPage } from '@/pages/insurer/CoverDetail';
import { AuditPage } from '@/pages/insurer/Audit';
import { ProfilePage } from '@/pages/Profile';
import { SettingsHub } from '@/pages/settings/Hub';
import { DevToolsPage, SystemHealthPage } from '@/pages/insurer/DevTools';
import { TemplatesPage } from '@/pages/insurer/Templates';
import { BackupsPage } from '@/pages/insurer/Backups';
import { TemplateEditorPage } from '@/pages/insurer/TemplateEditor';
import { InstructionsPage } from '@/pages/insurer/Instructions';
import { BulkDispatchPage } from '@/pages/insurer/BulkDispatch';
import { AnalyticsPage } from '@/pages/insurer/Analytics';
import { AlertsPage } from '@/pages/insurer/Alerts';
import { ImportsPage } from '@/pages/insurer/Imports';
import { ExcelImportWizardPage } from '@/pages/insurer/ExcelImportWizard';
import { RiskPage } from '@/pages/insurer/Risk';
import { ExportsPage } from '@/pages/insurer/Exports';
import { SurveyFillPage } from '@/pages/public/SurveyFill';
import { PublicRegistration } from '@/pages/PublicRegistration';
import { BrokerDashboard } from '@/pages/broker/Dashboard';
import { BrokerClients } from '@/pages/broker/Clients';
import { BrokerSurveyReview } from '@/pages/broker/SurveyReview';
import { IssueSurveyPage } from '@/pages/insurer/IssueSurvey';
import { ClientLayout } from '@/layouts/ClientLayout';
import { ClientAllocate } from '@/pages/client/Allocate';
import { MasterLayout } from '@/layouts/MasterLayout';
import { MasterOverview } from '@/pages/master/Overview';
import { MasterInsurers } from '@/pages/master/Insurers';
import { MasterUsers } from '@/pages/master/Users';
import { MasterAudit } from '@/pages/master/Audit';
import { MasterSwitch } from '@/pages/master/Switch';
import { MasterSettings } from '@/pages/master/Settings';

const RoleRoute: React.FC<{ context: 'insurer' | 'broker' | 'master' | 'client'; children: React.ReactNode }> = ({ context, children }) => {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-raptor-navy/60 font-body">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.must_reset_password) return <Navigate to="/force-reset" replace />;
  if (user.context !== context) {
    const dest = user.context === 'broker' ? '/broker' : user.context === 'master' ? '/master' : user.context === 'client' ? '/client' : '/insurer';
    return <Navigate to={dest} replace />;
  }
  return <>{children}</>;
};

const RootRedirect: React.FC = () => {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-raptor-navy/60 font-body">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.must_reset_password) return <Navigate to="/force-reset" replace />;
  const dest = user.context === 'broker' ? '/broker' : user.context === 'master' ? '/master' : user.context === 'client' ? '/client' : '/insurer';
  return <Navigate to={dest} replace />;
};

const App: React.FC = () => {
  return (
    <BrandingProvider>
    <div className="h-screen flex flex-col">
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<RootRedirect />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/force-reset" element={<ForceResetPage />} />
          <Route path="/r/:token" element={<PublicRegistration />} />
          <Route path="/s/:token" element={<SurveyFillPage />} />
          <Route path="/intake/:token" element={<SiteIntakePage />} />

          <Route path="/insurer" element={<RoleRoute context="insurer"><InsurerLayout /></RoleRoute>}>
            <Route index element={<InsurerDashboard />} />
            <Route path="brokers" element={<BrokersPage />} />
            <Route path="brokers/:brokerId" element={<BrokerDetailPage />} />
            <Route path="templates" element={<TemplatesPage />} />
            <Route path="templates/:templateId" element={<TemplateEditorPage />} />
            <Route path="instructions" element={<InstructionsPage />} />
            <Route path="instructions/issue" element={<IssueSurveyPage />} />
            <Route path="instructions/bulk" element={<BulkDispatchPage />} />
            <Route path="analytics" element={<AnalyticsPage />} />
            <Route path="alerts" element={<AlertsPage />} />
            <Route path="risk" element={<RiskPage />} />
            <Route path="exports" element={<ExportsPage />} />
            <Route path="imports" element={<ImportsPage />} />
            <Route path="imports/excel/new" element={<ExcelImportWizardPage />} />
            <Route path="imports/excel/:jobId" element={<ExcelImportWizardPage />} />
            <Route path="clients" element={<ClientsPage />} />
            <Route path="clients/:clientId" element={<ClientDetailPage />} />
            <Route path="policies" element={<CoversPage />} />
            <Route path="covers" element={<CoversPage />} />
            <Route path="covers/new" element={<CoverBuilderPage />} />
            <Route path="covers/:coverId" element={<CoverDetailPage />} />
            <Route path="covers/:coverId/edit" element={<CoverBuilderPage />} />
            <Route path="sites" element={<SitesPage />} />
            <Route path="pending-sites" element={<PendingSitesPage />} />
            <Route path="sites/:siteId" element={<SiteDetailPage />} />
            <Route path="audit" element={<AuditPage />} />
            <Route path="dev-tools" element={<DevToolsPage />} />
            <Route path="system-health" element={<SystemHealthPage />} />
            <Route path="backups" element={<BackupsPage />} />
            <Route path="profile" element={<ProfilePage />} />
            <Route path="settings/*" element={<SettingsHub />} />
          </Route>

          <Route path="/broker" element={<RoleRoute context="broker"><BrokerLayout /></RoleRoute>}>
            <Route index element={<BrokerDashboard />} />
            <Route path="clients" element={<BrokerClients />} />
            <Route path="surveys" element={<BrokerSurveyReview />} />
            <Route path="profile" element={<ProfilePage />} />
          </Route>

          <Route path="/client" element={<RoleRoute context="client"><ClientLayout /></RoleRoute>}>
            <Route index element={<ClientAllocate />} />
            <Route path="profile" element={<ProfilePage />} />
          </Route>

          <Route path="/master" element={<RoleRoute context="master"><MasterLayout /></RoleRoute>}>
            <Route index element={<MasterOverview />} />
            <Route path="insurers" element={<MasterInsurers />} />
            <Route path="users" element={<MasterUsers />} />
            <Route path="audit" element={<MasterAudit />} />
            <Route path="switch" element={<MasterSwitch />} />
            <Route path="settings" element={<MasterSettings />} />
            <Route path="profile" element={<ProfilePage />} />
          </Route>

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </div>
    </BrandingProvider>
  );
};

export default App;
